import FieldResponse from './FieldResponse';
import FormResponseSet from './FormResponseSet';
import { createError } from '../error';

class PageResponse {
  constructor(data) {
    data = data || {};
    this.user_role_id = data.user_role_id;
    this.current_location = data.current_location;

    this.recapResponse = data.recapResponse;
    if (!this.recapResponse) {
      throw createError({
        name: "NoRecapResponseProvided",
        message: "Recap Response is required for page response",
      });
    }

    this.page = data.page;
    if (!this.page) {
      throw createError({
        name: "NoPageProvided",
        message: "Page is required for page response",
      });
    }

    this.field_responses = data.field_responses || [];
    this.form_response_sets = data.form_response_sets || [];

    this._populateAllResponses();
  }

  get isLastPage() {
    return this.recapResponse.isLastPage(this.page);
  }

  get isFirstPage() {
    return this.recapResponse.isFirstPage(this.page);
  }

  get visible() {
    return this.field_responses.some(fieldResponse => fieldResponse.visible)
      || this.form_response_sets.some(formResponseSet => formResponseSet.visible);
  }

  set valid(valid) {
    this._valid = valid;
  }

  get valid() {
    return this._valid;
  }

  set touched(touched) {
    this._touched = touched;
  }

  get touched() {
    return this._touched;
  }

  set viewed(viewed) {
    this._viewed = viewed;
  }

  get viewed() {
    return this._viewed;
  }

  touch() {
    this.touched = true;
  }

  view() {
    this.viewed = true;
  }

  getFormResponseSet(form) {
    let formResponseSet = this.form_response_sets.find(response => response.form.id === form.id);
    if (!formResponseSet) {
      formResponseSet = new FormResponseSet({
        form: form,
        user_role_id: this.user_role_id,
        current_location: this.current_location,
        events_custom_responses_id: this.recapResponse.id,
        recapResponse: this.recapResponse,
      });
      this.form_response_sets.push(formResponseSet);
    }
    return formResponseSet;
  }

  getFieldResponse(fieldInstance) {
    let fieldResponse = this.field_responses.find(response => response.events_custom_instances_id == fieldInstance.id);
    if (!fieldResponse) {
      fieldResponse = new FieldResponse({
        events_custom_instances_id: fieldInstance.id,
        user_role_id: this.user_role_id,
        current_location: this.current_location,
        fieldInstance: fieldInstance,
        recapResponse: this.recapResponse,
      });
      this.field_responses.push(fieldResponse);
    }
    return fieldResponse;
  }

  collectValues() {
    return this.collectVisibleFormResponseSets().map(formResponseSet => formResponseSet.collectValues());
  }

  validate() {
    let errors = [];
    errors = errors
      .concat(
        this.collectVisibleFormResponseSets()
          .map(formResponseSet => formResponseSet.validate())
          .reduce((pre, cur) => pre.concat(cur), [])
      );

    this.valid = errors.length === 0;
    return errors;
  }

  updateDisplayLogic(changedFieldResponses = null) {
    const results = [];
    for (const fieldResponse of this.field_responses) {
      results.push(fieldResponse.updateDisplayLogic(changedFieldResponses));
    }
    for (const formResponseSet of this.form_response_sets) {
      results.push(formResponseSet.updateDisplayLogic(changedFieldResponses));
    }
    return results.some(r => r);
  }

  updateLogicModifiers() {
    for (const fieldResponse of this.field_responses) {
      fieldResponse.checkLogicModifiers();
    }
    for (const formResponseSet of this.form_response_sets) {
      formResponseSet.updateLogicModifiers();
    }
  }

  collectVisibleFormResponses() {
    return this.collectVisibleFormResponseSets()
      .map(formResponseSet => formResponseSet.collectVisibleFormResponses())
      .reduce((pre, cur) => pre.concat(cur), []);
  }

  collectVisibleFormResponseSets() {
    return this.form_response_sets.filter(formResponseSet => formResponseSet.visible);
  }

  _populateAllResponses() {
    const allResponses = [];
    for (const item of this.page.items) {
      let response;
      if (item.isForm) {
        response = this.getFormResponseSet(item);
      } else {
        response = this.getFieldResponse(item);
      }
      allResponses.push(response);
    }
    return allResponses;
  }
}

export default PageResponse;
